/**
 * Voice Call Approval Card
 *
 * The gate before Klaris dials a real telephone. Shown inline in chat when
 * the agent wants to make a call, and nothing is dialed until someone here
 * says so.
 *
 * The number is editable because it is the likeliest thing to be wrong —
 * the agent usually read it off a document, and a mistyped digit means
 * ringing a stranger rather than a carrier.
 */

import React, { useState } from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PhoneForwardedRoundedIcon from '@mui/icons-material/PhoneForwardedRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import type { VoiceCallApproval } from '../../../types/voiceCall';

interface Props {
  approval: VoiceCallApproval;
  onApprove: () => void;
  onEditNumber: (editedNumber: string) => void;
  onReject: (reason: string) => void;
}

export default function VoiceCallApprovalCard({
  approval,
  onApprove,
  onEditNumber,
  onReject,
}: Props) {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editedNumber, setEditedNumber] = useState(approval.phoneNumber);

  const numberChanged = editedNumber.trim() !== approval.phoneNumber;

  const labelSx = {
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: theme.tokens.text.tertiary,
  };

  return (
    <Box
      sx={{
        flexShrink: 0,   // flex column parent; see VoiceCallCard
        border: `1px solid ${theme.tokens.border.default}`,
        borderRadius: `${theme.ds.radius.lg}px`,
        bgcolor: theme.tokens.surface.raised,
        boxShadow: theme.tokens.shadow.xs,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${theme.tokens.border.subtle}`,
          bgcolor: theme.tokens.surface.sunken,
        }}
      >
        <PhoneForwardedRoundedIcon
          sx={{ fontSize: 17, color: theme.tokens.text.accent }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Place a call to {approval.carrierName}?
        </Typography>
      </Stack>

      <Stack spacing={2} sx={{ px: 2, py: 2 }}>
        <Box>
          <Typography sx={labelSx}>Number</Typography>
          {isEditing ? (
            <TextField
              value={editedNumber}
              onChange={(e) => setEditedNumber(e.target.value)}
              size="small"
              fullWidth
              autoFocus
              placeholder="+919876543210"
              helperText="International format, including the country code"
              sx={{
                mt: 0.75,
                '& input': {
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: '0.85rem',
                },
              }}
            />
          ) : (
            <Typography
              sx={{
                mt: 0.5,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.9rem',
              }}
            >
              {approval.phoneNumber}
            </Typography>
          )}
        </Box>

        <Box>
          <Typography sx={labelSx}>What the call should establish</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: theme.tokens.text.secondary }}>
            {approval.objective}
          </Typography>
        </Box>

        {approval.policyNumber && (
          <Box>
            <Typography sx={labelSx}>Policy</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: theme.tokens.text.secondary }}>
              {approval.policyNumber}
              {approval.policyholder ? ` · ${approval.policyholder}` : ''}
            </Typography>
          </Box>
        )}

        <Box>
          <Typography sx={labelSx}>What the agent can verify with</Typography>
          {approval.knownFields && approval.knownFields.length > 0 ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
              {approval.knownFields.map((f) => (
                <Chip
                  key={f}
                  size="small"
                  label={f}
                  sx={{ fontSize: '0.68rem', height: 22 }}
                />
              ))}
            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={{ mt: 0.5, color: theme.tokens.semantic.warning }}
            >
              Nothing — the carrier will most likely refuse to discuss the policy.
              {approval.factsNote ? ` ${approval.factsNote}` : ''}
            </Typography>
          )}
        </Box>

        <Typography variant="caption" sx={{ color: theme.tokens.text.tertiary }}>
          A real phone will ring and an AI agent will speak to whoever answers.
          {approval.numberIsDefault
            ? ' This number is the workspace default, not a directory entry for this carrier.'
            : ''}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<PhoneForwardedRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={() => {
              if (isEditing && numberChanged) onEditNumber(editedNumber.trim());
              else onApprove();
            }}
            disabled={isEditing && !editedNumber.trim()}
          >
            {isEditing && numberChanged ? 'Call this number' : 'Place the call'}
          </Button>
          {!isEditing && (
            <Button
              variant="text"
              size="small"
              startIcon={<EditRoundedIcon sx={{ fontSize: 15 }} />}
              onClick={() => setIsEditing(true)}
            >
              Change number
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="text"
            size="small"
            color="inherit"
            startIcon={<CloseRoundedIcon sx={{ fontSize: 15 }} />}
            onClick={() => onReject('The analyst chose not to place the call')}
            sx={{ color: theme.tokens.text.secondary }}
          >
            Don&apos;t call
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
